import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import axios from "axios";

const AuthContext = createContext(null);

// Must stay in sync with backend/auth.py IDLE_TIMEOUT_SECONDS.
const IDLE_TIMEOUT_MS = 60 * 60 * 1000; // 1 hour of inactivity
const CHECK_INTERVAL_MS = 60 * 1000;
const HAD_SESSION_KEY = "draconia_had_session";
const ACTIVITY_EVENTS = ["mousemove", "keydown", "touchstart", "scroll", "click"];

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loggedOutReason, setLoggedOutReason] = useState(null);
  const lastActivityRef = useRef(Date.now());

  const refresh = useCallback(async () => {
    try {
      const { data } = await axios.get("/api/me");
      setUser(data);
      localStorage.setItem(HAD_SESSION_KEY, "1");
      return true;
    } catch {
      const hadSession = localStorage.getItem(HAD_SESSION_KEY) === "1";
      setUser(null);
      if (hadSession) {
        setLoggedOutReason("idle");
        localStorage.removeItem(HAD_SESSION_KEY);
      }
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    const markActive = () => {
      lastActivityRef.current = Date.now();
    };
    ACTIVITY_EVENTS.forEach((evt) => window.addEventListener(evt, markActive, { passive: true }));
    return () => {
      ACTIVITY_EVENTS.forEach((evt) => window.removeEventListener(evt, markActive));
    };
  }, []);

  // While logged in: renew the session on real activity, or log out once idle
  // for too long - checked against wall-clock time so it self-corrects after
  // the machine sleeps, unlike a plain setTimeout.
  useEffect(() => {
    if (!user) return undefined;

    const interval = setInterval(async () => {
      const idleFor = Date.now() - lastActivityRef.current;
      if (idleFor >= IDLE_TIMEOUT_MS) {
        try {
          await axios.post("/api/logout");
        } catch {
          // Cookie may already be gone server-side; state cleanup below still applies.
        }
        setUser(null);
        setLoggedOutReason("idle");
        localStorage.removeItem(HAD_SESSION_KEY);
        return;
      }
      await refresh();
    }, CHECK_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [user, refresh]);

  const login = async (username, password) => {
    await axios.post("/api/login", { username, password });
    lastActivityRef.current = Date.now();
    setLoggedOutReason(null);
    await refresh();
  };

  const logout = async () => {
    await axios.post("/api/logout");
    setUser(null);
    setLoggedOutReason(null);
    localStorage.removeItem(HAD_SESSION_KEY);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, loggedOutReason, refreshUser: refresh }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
